import React, { useState, useEffect, useCallback } from 'react';
import { jobMaterialsAPI, vansAPI, warehouseAPI, itemsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Package, Plus, Undo2, Loader2, AlertTriangle, Search, Info, CheckCircle2, Clock, ArrowRight, TrendingUp, RotateCcw, ListChecks, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// Status badge config — Phase 3: explicit deduction state distinction
// ---------------------------------------------------------------------------
const BADGE_CONFIG = {
  expected:                { label: 'Expected',       cls: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' },
  used:                    { label: 'Deducted',       cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  confirmed:               { label: 'Deducted',       cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  confirmed_no_deduction:  { label: 'No Deduction',   cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  overused:                { label: 'Overused',       cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  shortage:                { label: 'Shortage',       cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  reversed:                { label: 'Reversed',       cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

function StatusBadge({ status }) {
  const cfg = BADGE_CONFIG[status] || BADGE_CONFIG.used;
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${cfg.cls}`} data-testid={`status-badge-${status}`}>
      {cfg.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main Tab — Phase 3: Enhanced with bulk confirm + deduction awareness
// ---------------------------------------------------------------------------
export function JobMaterialsTab({ jobId, onChanged }) {
  const { isOwner, isOffice } = useAuth();
  const canEdit = isOwner || isOffice;

  const [materials, setMaterials] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [expectedCount, setExpectedCount] = useState(0);
  const [skippedDeductionCount, setSkippedDeductionCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showReversed, setShowReversed] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [returnTarget, setReturnTarget] = useState(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [quickConfirming, setQuickConfirming] = useState(null); // material id being quick-confirmed

  const fetchMaterials = useCallback(async () => {
    try {
      const res = await jobMaterialsAPI.list(jobId, { include_reversed: showReversed });
      setMaterials(res.data.materials || []);
      setTotalCost(res.data.total_cost || 0);
      setExpectedCount(res.data.expected_count || 0);
      setSkippedDeductionCount(res.data.skipped_deduction_count || 0);
    } catch {
      toast.error('Failed to load materials');
    } finally {
      setLoading(false);
    }
  }, [jobId, showReversed]);

  useEffect(() => { fetchMaterials(); }, [fetchMaterials]);

  const handleUndo = async (materialId) => {
    try {
      await jobMaterialsAPI.reverse(jobId, materialId);
      toast.success('Material reversed — stock restored');
      fetchMaterials();
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reverse material');
    }
  };

  // Quick confirm: one-tap confirm using auto-detect (no modal)
  const handleQuickConfirm = async (material) => {
    setQuickConfirming(material.id);
    try {
      const res = await jobMaterialsAPI.confirm(jobId, material.id, {
        qty: material.expected_qty || material.qty,
      });
      const data = res.data;
      if (data.deduction_skipped) {
        toast.warning(`${material.item_name} confirmed — but inventory was NOT deducted (no vehicle assigned)`);
      } else {
        toast.success(`${material.item_name} confirmed — ${data.qty} deducted`);
      }
      fetchMaterials();
      if (onChanged) onChanged();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'object' && detail?.code === 'INSUFFICIENT_STOCK') {
        toast.error(`Insufficient stock for ${material.item_name}. Available: ${detail.available}`);
      } else if (typeof detail === 'string' && detail.includes('not in expected state')) {
        toast.info(`${material.item_name} already confirmed`);
        fetchMaterials();
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to confirm');
      }
    } finally {
      setQuickConfirming(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  }

  const expectedMaterials = materials.filter(m => m.is_expected && !m.is_reversed);
  const usedMaterials = materials.filter(m => !m.is_expected);
  const skippedMaterials = usedMaterials.filter(m => m.deduction_skipped && !m.is_reversed);

  return (
    <div className="space-y-5" data-testid="job-materials-tab">
      {/* --- Deduction Skipped Warning Banner --- */}
      {(skippedDeductionCount > 0 || skippedMaterials.length > 0) && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/50" data-testid="deduction-skipped-banner">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              {skippedMaterials.length} material{skippedMaterials.length !== 1 ? 's' : ''} confirmed without inventory deduction
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
              No vehicle was assigned when these were confirmed. Assign a vehicle to this job and re-confirm, or manually deduct from inventory.
            </p>
          </div>
        </div>
      )}

      {/* --- Expected Materials Section --- */}
      {expectedMaterials.length > 0 && (
        <div data-testid="expected-materials-section">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-sky-500" />
              <h3 className="text-sm font-semibold tracking-tight">Expected Materials</h3>
              <span className="text-xs bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400 px-2 py-0.5 rounded-full font-medium">
                {expectedMaterials.length} pending
              </span>
            </div>
            {canEdit && expectedMaterials.length > 1 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setBulkConfirmOpen(true)}
                className="text-sky-700 hover:text-sky-800 hover:bg-sky-50 border-sky-200 dark:text-sky-400 dark:hover:bg-sky-950/30 dark:border-sky-800"
                data-testid="bulk-confirm-btn"
              >
                <ListChecks className="w-4 h-4 mr-1.5" /> Confirm All ({expectedMaterials.length})
              </Button>
            )}
          </div>
          <div className="space-y-2">
            {expectedMaterials.map((m) => (
              <ExpectedRow
                key={m.id}
                material={m}
                canEdit={canEdit}
                onConfirm={() => setConfirmTarget(m)}
                onQuickConfirm={() => handleQuickConfirm(m)}
                onUndo={() => handleUndo(m.id)}
                quickConfirming={quickConfirming === m.id}
              />
            ))}
          </div>
        </div>
      )}

      {/* --- Used / Confirmed Materials Section --- */}
      <div data-testid="used-materials-section">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <h3 className="text-sm font-semibold tracking-tight">Materials Used</h3>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help text-muted-foreground" data-testid="materials-used-tooltip">
                      <Info className="w-3.5 h-3.5" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    Materials are deducted from the selected source. If no vehicle is assigned during auto-detect, inventory is not deducted and the material is flagged.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {totalCost > 0 && (
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium" data-testid="materials-total-cost">
                ${totalCost.toFixed(2)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={showReversed} onChange={(e) => setShowReversed(e.target.checked)} className="rounded" />
              Show reversed
            </label>
            {canEdit && (
              <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-material-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Material
              </Button>
            )}
          </div>
        </div>

        {usedMaterials.length === 0 && expectedMaterials.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            <Package className="w-6 h-6 mx-auto mb-1 opacity-40" />
            <p className="text-xs">No materials recorded yet.</p>
          </div>
        ) : usedMaterials.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            <p className="text-xs">No confirmed usage yet. Confirm expected materials above to deduct inventory.</p>
          </div>
        ) : (
          <div className="space-y-2 mt-2">
            {usedMaterials.map((m) => (
              <MaterialRow key={m.id} material={m} canEdit={canEdit} onUndo={handleUndo} onReturn={() => setReturnTarget(m)} />
            ))}
          </div>
        )}
      </div>

      {/* --- Modals --- */}
      {addOpen && (
        <AddMaterialModal jobId={jobId} open={addOpen} onClose={() => setAddOpen(false)} onAdded={() => { fetchMaterials(); if (onChanged) onChanged(); }} />
      )}
      {confirmTarget && (
        <ConfirmMaterialModal jobId={jobId} material={confirmTarget} open={!!confirmTarget} onClose={() => setConfirmTarget(null)} onConfirmed={() => { setConfirmTarget(null); fetchMaterials(); if (onChanged) onChanged(); }} />
      )}
      {returnTarget && (
        <ReturnMaterialModal jobId={jobId} material={returnTarget} open={!!returnTarget} onClose={() => setReturnTarget(null)} onReturned={() => { setReturnTarget(null); fetchMaterials(); if (onChanged) onChanged(); }} />
      )}
      {bulkConfirmOpen && (
        <BulkConfirmModal
          jobId={jobId}
          expectedMaterials={expectedMaterials}
          open={bulkConfirmOpen}
          onClose={() => setBulkConfirmOpen(false)}
          onDone={() => { setBulkConfirmOpen(false); fetchMaterials(); if (onChanged) onChanged(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Expected Material Row — Phase 3: one-tap quick confirm + full modal option
// ---------------------------------------------------------------------------
function ExpectedRow({ material: m, canEdit, onConfirm, onQuickConfirm, onUndo, quickConfirming }) {
  const [undoing, setUndoing] = useState(false);

  return (
    <Card className="border-sky-200 dark:border-sky-800/50 bg-sky-50/30 dark:bg-sky-950/10" data-testid={`expected-row-${m.id}`}>
      <CardContent className="py-3 px-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium truncate" data-testid="expected-item-name">{m.item_name || 'Unknown Item'}</span>
              {m.item_sku && <span className="text-xs text-muted-foreground">({m.item_sku})</span>}
              <StatusBadge status={m.status_badge} />
              {m.generation_source && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-medium">
                  {m.generation_source === 'direct' ? 'Direct' : m.generation_source === 'recipe' ? 'Recipe' : 'Manual'}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-xs text-muted-foreground">
              <span data-testid="expected-qty">Expected Qty: <strong className="text-foreground">{m.expected_qty || m.qty}</strong></span>
              {m.unit_cost_at_time != null && (
                <span>Est. Cost: <strong className="text-foreground">${((m.expected_qty || m.qty) * m.unit_cost_at_time).toFixed(2)}</strong></span>
              )}
              {m.notes && <span className="italic truncate max-w-[200px]">"{m.notes}"</span>}
            </div>
          </div>
          {canEdit && (
            <div className="flex items-center gap-1.5 shrink-0">
              {/* Quick confirm — one-tap, uses auto-detect, accepts expected qty */}
              <Button
                size="sm"
                onClick={onQuickConfirm}
                disabled={quickConfirming}
                data-testid={`quick-confirm-btn-${m.id}`}
              >
                {quickConfirming ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <><CheckCircle2 className="w-4 h-4 mr-1" /> Confirm</>
                )}
              </Button>
              {/* Full modal — for adjusting qty or picking a specific source */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="sm" onClick={onConfirm} className="px-2" data-testid={`adjust-confirm-btn-${m.id}`}>
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Adjust qty or pick source</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button variant="ghost" size="sm" onClick={async () => { setUndoing(true); await onUndo(); setUndoing(false); }} disabled={undoing} className="text-destructive hover:text-destructive px-2" data-testid={`remove-expected-${m.id}`}>
                {undoing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Used / Confirmed Material Row — Phase 3: enhanced deduction-skipped display
// ---------------------------------------------------------------------------
function MaterialRow({ material: m, canEdit, onUndo, onReturn }) {
  const [undoing, setUndoing] = useState(false);

  const handleUndo = async () => {
    setUndoing(true);
    await onUndo(m.id);
    setUndoing(false);
  };

  const addedDate = m.added_at ? new Date(m.added_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
  const lineCost = m.unit_cost_at_time != null ? (m.qty * m.unit_cost_at_time).toFixed(2) : null;
  const hasVariance = m.confirmed_at && m.expected_qty && m.qty !== m.expected_qty;
  const variance = hasVariance ? m.qty - m.expected_qty : 0;

  // Border color based on deduction state
  const borderCls = m.is_reversed
    ? 'opacity-50 border-dashed'
    : m.deduction_skipped
      ? 'border-amber-300 dark:border-amber-700 bg-amber-50/30 dark:bg-amber-950/10'
      : '';

  return (
    <Card className={borderCls} data-testid={`material-row-${m.id}`}>
      <CardContent className="py-3 px-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium truncate" data-testid="material-item-name">{m.item_name || 'Unknown Item'}</span>
              {m.item_sku && <span className="text-xs text-muted-foreground">({m.item_sku})</span>}
              <StatusBadge status={m.status_badge} />
              {m.deduction_skipped && !m.is_reversed && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="flex items-center gap-0.5 text-xs text-amber-600 dark:text-amber-400 cursor-help" data-testid={`skipped-badge-${m.id}`}>
                        <AlertCircle className="w-3.5 h-3.5" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">{m.deduction_warning || 'Inventory was not deducted. No vehicle was assigned.'}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {m.auto_deducted && !m.is_reversed && !m.deduction_skipped && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-xs bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 px-1.5 py-0.5 rounded font-medium cursor-help" data-testid={`auto-deduct-badge-${m.id}`}>
                        Auto
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>Auto-deducted from the assigned vehicle's inventory.</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {m.generation_source && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-medium">
                  {m.generation_source === 'direct' ? 'Direct' : m.generation_source === 'recipe' ? 'Recipe' : 'Manual'}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-xs text-muted-foreground">
              <span data-testid="material-qty">Qty: <strong className="text-foreground">{m.qty}</strong></span>
              {hasVariance && (
                <span className={`flex items-center gap-0.5 font-medium ${variance > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`} data-testid="material-variance">
                  <TrendingUp className="w-3 h-3" />
                  expected {m.expected_qty}, used {m.qty} ({variance > 0 ? '+' : ''}{variance})
                </span>
              )}
              {m.source_name && <span data-testid="material-source">From: <strong className="text-foreground">{m.source_name}</strong></span>}
              {lineCost && <span data-testid="material-line-cost">Cost: <strong className="text-foreground">${lineCost}</strong></span>}
              {m.added_by_name && <span>By: {m.added_by_name}</span>}
              {m.confirmed_by_name && <span>Confirmed: {m.confirmed_by_name}</span>}
              {addedDate && <span>{addedDate}</span>}
              {m.notes && <span className="italic truncate max-w-[200px]">"{m.notes}"</span>}
            </div>
          </div>
          {canEdit && !m.is_reversed && (
            <div className="flex items-center gap-1 shrink-0">
              <Button variant="outline" size="sm" onClick={onReturn} className="text-teal-600 hover:text-teal-700 hover:bg-teal-50 border-teal-200" data-testid={`return-material-${m.id}`}>
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Return
              </Button>
              <Button variant="ghost" size="sm" onClick={handleUndo} disabled={undoing} className="text-destructive hover:text-destructive shrink-0" data-testid={`undo-material-${m.id}`}>
                {undoing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
                <span className="ml-1 hidden sm:inline">Undo</span>
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Bulk Confirm Modal — Phase 3: confirm all expected materials at once
// ---------------------------------------------------------------------------
function BulkConfirmModal({ jobId, expectedMaterials, open, onClose, onDone }) {
  const { isOwner } = useAuth();
  const [useAutoDeduct, setUseAutoDeduct] = useState(true);
  const [sourceType, setSourceType] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedSourceId, setSelectedSourceId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null); // null = not submitted yet

  useEffect(() => {
    const load = async () => {
      try {
        const [vRes, wRes] = await Promise.all([
          vansAPI.list({ is_active: true }),
          warehouseAPI.listWarehouses(),
        ]);
        setVehicles(vRes.data?.vans || vRes.data || []);
        setWarehouses(wRes.data?.warehouses || wRes.data || []);
      } catch { /* non-critical */ }
    };
    load();
  }, []);

  const handleSubmit = async () => {
    if (!useAutoDeduct && !selectedSourceId) { toast.error('Select a source'); return; }

    setSubmitting(true);
    try {
      const payload = {
        material_ids: expectedMaterials.map(m => m.id),
      };
      if (!useAutoDeduct) {
        payload.source_type = sourceType;
        if (sourceType === 'vehicle') payload.source_vehicle_id = selectedSourceId;
        else payload.source_warehouse_id = selectedSourceId;
      }

      const res = await jobMaterialsAPI.bulkConfirm(jobId, payload);
      const data = res.data;
      setResults(data);

      // Show summary toast
      const s = data.summary;
      if (s.failed === 0 && s.skipped === 0) {
        toast.success(`All ${s.confirmed} materials confirmed and deducted`);
      } else if (s.confirmed > 0 && s.skipped > 0) {
        toast.warning(`${s.confirmed} deducted, ${s.skipped} confirmed without deduction`);
      } else if (s.confirmed > 0 && s.failed > 0) {
        toast.warning(`${s.confirmed} deducted, ${s.failed} failed`);
      } else if (s.skipped > 0 && s.failed === 0) {
        toast.warning(`${s.skipped} confirmed without inventory deduction (no vehicle assigned)`);
      } else {
        toast.error(`${s.failed} materials failed to confirm`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk confirm failed');
    } finally {
      setSubmitting(false);
    }
  };

  const sources = sourceType === 'vehicle' ? vehicles : warehouses;

  // Result view after bulk confirm completes
  if (results) {
    const s = results.summary;
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-lg" data-testid="bulk-confirm-results-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListChecks className="w-5 h-5 text-sky-500" />
              Bulk Confirm Results
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Summary counts */}
            <div className="grid grid-cols-4 gap-2">
              {s.confirmed > 0 && (
                <div className="text-center p-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800" data-testid="bulk-result-confirmed">
                  <div className="text-lg font-bold text-emerald-700 dark:text-emerald-400">{s.confirmed}</div>
                  <div className="text-[10px] text-emerald-600 dark:text-emerald-500">Deducted</div>
                </div>
              )}
              {s.skipped > 0 && (
                <div className="text-center p-2 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800" data-testid="bulk-result-skipped">
                  <div className="text-lg font-bold text-amber-700 dark:text-amber-400">{s.skipped}</div>
                  <div className="text-[10px] text-amber-600 dark:text-amber-500">No Deduction</div>
                </div>
              )}
              {s.failed > 0 && (
                <div className="text-center p-2 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800" data-testid="bulk-result-failed">
                  <div className="text-lg font-bold text-red-700 dark:text-red-400">{s.failed}</div>
                  <div className="text-[10px] text-red-600 dark:text-red-500">Failed</div>
                </div>
              )}
              {s.already_confirmed > 0 && (
                <div className="text-center p-2 rounded-lg bg-slate-50 dark:bg-slate-950/20 border border-slate-200 dark:border-slate-800" data-testid="bulk-result-already">
                  <div className="text-lg font-bold text-slate-700 dark:text-slate-400">{s.already_confirmed}</div>
                  <div className="text-[10px] text-slate-600 dark:text-slate-500">Already Done</div>
                </div>
              )}
            </div>

            {/* Per-material results */}
            <div className="max-h-60 overflow-y-auto space-y-1.5" data-testid="bulk-results-list">
              {results.results.map((r) => (
                <div key={r.material_id} className={`flex items-center gap-2 text-sm p-2 rounded-md ${
                  r.status === 'confirmed' ? 'bg-emerald-50/50 dark:bg-emerald-950/10' :
                  r.status === 'skipped' ? 'bg-amber-50/50 dark:bg-amber-950/10' :
                  r.status === 'failed' ? 'bg-red-50/50 dark:bg-red-950/10' :
                  'bg-slate-50/50 dark:bg-slate-950/10'
                }`} data-testid={`bulk-result-${r.material_id}`}>
                  {r.status === 'confirmed' && <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />}
                  {r.status === 'skipped' && <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />}
                  {r.status === 'failed' && <XCircle className="w-4 h-4 text-red-500 shrink-0" />}
                  {r.status === 'already_confirmed' && <CheckCircle2 className="w-4 h-4 text-slate-400 shrink-0" />}
                  <span className="font-medium truncate">{r.item_name || r.material_id.slice(0, 8)}</span>
                  {r.qty && <span className="text-xs text-muted-foreground">x{r.qty}</span>}
                  {r.warning && <span className="text-xs text-amber-600 dark:text-amber-400 truncate">{r.warning}</span>}
                  {r.error && <span className="text-xs text-red-600 dark:text-red-400 truncate">{r.error}</span>}
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={onDone} data-testid="bulk-confirm-done-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Pre-confirm view
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="bulk-confirm-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-sky-500" />
            Confirm All Expected Materials
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Preview list */}
          <div className="p-3 rounded-lg bg-muted/50 border space-y-1.5 max-h-40 overflow-y-auto" data-testid="bulk-confirm-preview">
            {expectedMaterials.map(m => (
              <div key={m.id} className="flex items-center justify-between text-sm">
                <span className="truncate">{m.item_name}</span>
                <span className="text-muted-foreground shrink-0 ml-2">x{m.expected_qty || m.qty}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            {expectedMaterials.length} material{expectedMaterials.length !== 1 ? 's' : ''} will be confirmed at their expected quantities.
          </p>

          {/* Source selection */}
          <div>
            <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer">
              <input type="checkbox" checked={useAutoDeduct} onChange={(e) => setUseAutoDeduct(e.target.checked)} className="rounded" data-testid="bulk-auto-deduct-toggle" />
              Auto-detect source from assigned vehicle
            </label>
            {!useAutoDeduct && (
              <div className="space-y-3">
                <div>
                  <Label>Source Type</Label>
                  <Select value={sourceType} onValueChange={(v) => { setSourceType(v); setSelectedSourceId(''); }}>
                    <SelectTrigger data-testid="bulk-source-type"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vehicle">Vehicle</SelectItem>
                      <SelectItem value="warehouse">Warehouse</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {sourceType && (
                  <div>
                    <Label>{sourceType === 'vehicle' ? 'Vehicle' : 'Warehouse'}</Label>
                    <Select value={selectedSourceId} onValueChange={setSelectedSourceId}>
                      <SelectTrigger data-testid="bulk-source-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        {sources.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || (!useAutoDeduct && !selectedSourceId)} data-testid="submit-bulk-confirm-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ListChecks className="w-4 h-4 mr-1" />}
            Confirm {expectedMaterials.length} Materials
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Confirm Expected Material Modal — Phase 3: improved feedback
// ---------------------------------------------------------------------------
function ConfirmMaterialModal({ jobId, material, open, onClose, onConfirmed }) {
  const { isOwner } = useAuth();
  const [qty, setQty] = useState(String(material.expected_qty || material.qty));
  const [sourceType, setSourceType] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedSourceId, setSelectedSourceId] = useState('');
  const [availableQty, setAvailableQty] = useState(null);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [useAutoDeduct, setUseAutoDeduct] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [vRes, wRes] = await Promise.all([
          vansAPI.list({ is_active: true }),
          warehouseAPI.listWarehouses(),
        ]);
        setVehicles(vRes.data?.vans || vRes.data || []);
        setWarehouses(wRes.data?.warehouses || wRes.data || []);
      } catch { /* non-critical */ }
    };
    load();
  }, []);

  useEffect(() => {
    if (useAutoDeduct || !selectedSourceId || !material.item_id) { setAvailableQty(null); return; }
    const check = async () => {
      setLoadingAvail(true);
      try {
        if (sourceType === 'vehicle') {
          const res = await vansAPI.getInventory(selectedSourceId);
          const inv = (res.data?.inventory || res.data || []).find(i => i.item_id === material.item_id);
          setAvailableQty(inv ? (inv.qty_on_hand - (inv.quarantined_quantity || 0)) : 0);
        } else {
          const res = await warehouseAPI.getInventory({ location_id: selectedSourceId });
          const inv = (res.data?.items || res.data?.inventory || res.data || []).find(i => i.item_id === material.item_id);
          setAvailableQty(inv ? (inv.qty_on_hand - (inv.reserved_quantity || 0) - (inv.quarantined_quantity || 0)) : 0);
        }
      } catch { setAvailableQty(null); }
      setLoadingAvail(false);
    };
    check();
  }, [selectedSourceId, sourceType, material.item_id, useAutoDeduct]);

  const handleSubmit = async () => {
    const parsedQty = parseInt(qty, 10);
    if (!qty || isNaN(parsedQty) || parsedQty < 1) { toast.error('Qty must be >= 1'); return; }
    if (!useAutoDeduct && !selectedSourceId) { toast.error('Select a source'); return; }

    setSubmitting(true);
    try {
      const payload = { qty: parsedQty };
      if (!useAutoDeduct) {
        payload.source_type = sourceType;
        if (sourceType === 'vehicle') payload.source_vehicle_id = selectedSourceId;
        else payload.source_warehouse_id = selectedSourceId;
      }
      const res = await jobMaterialsAPI.confirm(jobId, material.id, payload);
      const data = res.data;
      if (data.deduction_skipped) {
        toast.warning(`${material.item_name} confirmed — but inventory was NOT deducted (no vehicle assigned)`);
      } else {
        toast.success(`${material.item_name} confirmed — ${data.qty} deducted from inventory`);
      }
      onConfirmed();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'object' && detail?.code === 'INSUFFICIENT_STOCK') {
        toast.error(`Insufficient stock. Available: ${detail.available}, Requested: ${detail.requested}`);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to confirm material');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const sources = sourceType === 'vehicle' ? vehicles : warehouses;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="confirm-material-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            Confirm Material Usage
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-3 rounded-lg bg-muted/50 border">
            <div className="text-sm font-medium" data-testid="confirm-item-name">{material.item_name}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Expected: {material.expected_qty || material.qty} units
              {material.unit_cost_at_time != null && ` | Est. cost: $${((material.expected_qty || material.qty) * material.unit_cost_at_time).toFixed(2)}`}
            </div>
          </div>

          <div>
            <Label>Actual Quantity Used</Label>
            <Input type="number" min="1" step="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="confirm-qty-input" />
            {parseInt(qty, 10) !== (material.expected_qty || material.qty) && !isNaN(parseInt(qty, 10)) && (
              <p className="text-xs mt-1 text-orange-600 dark:text-orange-400 flex items-center gap-1" data-testid="confirm-variance-note">
                <ArrowRight className="w-3 h-3" />
                Variance: {parseInt(qty, 10) - (material.expected_qty || material.qty) > 0 ? '+' : ''}{parseInt(qty, 10) - (material.expected_qty || material.qty)} from expected
              </p>
            )}
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer">
              <input type="checkbox" checked={useAutoDeduct} onChange={(e) => setUseAutoDeduct(e.target.checked)} className="rounded" data-testid="auto-deduct-toggle" />
              Auto-detect source from assigned vehicle
            </label>
            {!useAutoDeduct && (
              <div className="space-y-3">
                <div>
                  <Label>Source Type</Label>
                  <Select value={sourceType} onValueChange={(v) => { setSourceType(v); setSelectedSourceId(''); setAvailableQty(null); }}>
                    <SelectTrigger data-testid="confirm-source-type"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vehicle">Vehicle</SelectItem>
                      <SelectItem value="warehouse">Warehouse</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {sourceType && (
                  <div>
                    <Label>{sourceType === 'vehicle' ? 'Vehicle' : 'Warehouse'}</Label>
                    <Select value={selectedSourceId} onValueChange={setSelectedSourceId}>
                      <SelectTrigger data-testid="confirm-source-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        {sources.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {selectedSourceId && (
                  <div className="text-sm" data-testid="confirm-available-qty">
                    {loadingAvail ? (
                      <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Checking...</span>
                    ) : availableQty != null ? (
                      <span className={`font-medium ${availableQty >= parseInt(qty, 10) ? 'text-green-600' : 'text-amber-600'}`}>
                        Available: {availableQty}
                        {availableQty < parseInt(qty, 10) && ' (shortage will occur)'}
                      </span>
                    ) : null}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || (!useAutoDeduct && !selectedSourceId)} data-testid="submit-confirm-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
            Confirm & Deduct
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Return Material Modal
// ---------------------------------------------------------------------------
function ReturnMaterialModal({ jobId, material, open, onClose, onReturned }) {
  const [qty, setQty] = useState(String(material.qty));
  const [destType, setDestType] = useState(material.source_type || 'vehicle');
  const [vehicles, setVehicles] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedDestId, setSelectedDestId] = useState(material.source_vehicle_id || material.source_warehouse_id || '');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [vRes, wRes] = await Promise.all([
          vansAPI.list({ is_active: true }),
          warehouseAPI.listWarehouses(),
        ]);
        setVehicles(vRes.data?.vans || vRes.data || []);
        setWarehouses(wRes.data?.warehouses || wRes.data || []);
      } catch { /* non-critical */ }
    };
    load();
  }, []);

  const handleSubmit = async () => {
    const parsedQty = parseInt(qty, 10);
    if (!qty || isNaN(parsedQty) || parsedQty < 1) { toast.error('Qty must be >= 1'); return; }
    if (parsedQty > material.qty) { toast.error(`Cannot return more than ${material.qty} used`); return; }
    if (!selectedDestId) { toast.error('Select a destination'); return; }

    setSubmitting(true);
    try {
      await jobMaterialsAPI.returnToInventory(jobId, material.id, {
        qty: parsedQty,
        destination_type: destType,
        destination_vehicle_id: destType === 'vehicle' ? selectedDestId : undefined,
        destination_warehouse_id: destType === 'warehouse' ? selectedDestId : undefined,
      });
      toast.success('Material returned to inventory');
      onReturned();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to return material');
    } finally {
      setSubmitting(false);
    }
  };

  const destinations = destType === 'vehicle' ? vehicles : warehouses;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="return-material-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-teal-500" />
            Return to Inventory
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-3 rounded-lg bg-muted/50 border">
            <div className="text-sm font-medium" data-testid="return-item-name">{material.item_name}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Used: {material.qty} | Originally from: {material.source_name || material.source_type}
            </div>
          </div>
          <div>
            <Label>Quantity to Return</Label>
            <Input type="number" min="1" max={material.qty} value={qty} onChange={(e) => setQty(e.target.value)} data-testid="return-qty-input" />
            <p className="text-xs text-muted-foreground mt-1">Max: {material.qty}</p>
          </div>
          <div>
            <Label>Return Destination</Label>
            <Select value={destType} onValueChange={(v) => { setDestType(v); setSelectedDestId(''); }}>
              <SelectTrigger data-testid="return-dest-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="vehicle">Vehicle</SelectItem>
                <SelectItem value="warehouse">Warehouse</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{destType === 'vehicle' ? 'Vehicle' : 'Warehouse'}</Label>
            <Select value={selectedDestId} onValueChange={setSelectedDestId}>
              <SelectTrigger data-testid="return-dest-select"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>
                {destinations.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || !selectedDestId} data-testid="submit-return-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
            Return to Inventory
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Add Material Modal (existing)
// ---------------------------------------------------------------------------
function AddMaterialModal({ jobId, open, onClose, onAdded }) {
  const { isOwner } = useAuth();
  const [sourceType, setSourceType] = useState('vehicle');
  const [vehicles, setVehicles] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedSourceId, setSelectedSourceId] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [itemResults, setItemResults] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [qty, setQty] = useState('');
  const [notes, setNotes] = useState('');
  const [availableQty, setAvailableQty] = useState(null);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [vRes, wRes] = await Promise.all([
          vansAPI.list({ is_active: true }),
          warehouseAPI.listWarehouses(),
        ]);
        setVehicles(vRes.data?.vans || vRes.data || []);
        setWarehouses(wRes.data?.warehouses || wRes.data || []);
      } catch { /* non-critical */ }
    };
    load();
  }, []);

  useEffect(() => {
    if (!itemSearch || itemSearch.length < 2) { setItemResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await itemsAPI.search(itemSearch, 15);
        setItemResults(res.data?.items || res.data || []);
      } catch { /* ignore */ }
      setSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [itemSearch]);

  useEffect(() => {
    if (!selectedItem || !selectedSourceId) { setAvailableQty(null); return; }
    const check = async () => {
      setLoadingAvail(true);
      try {
        if (sourceType === 'vehicle') {
          const res = await vansAPI.getInventory(selectedSourceId);
          const inv = (res.data?.inventory || res.data || []).find(i => i.item_id === selectedItem.id);
          setAvailableQty(inv ? (inv.qty_on_hand - (inv.quarantined_quantity || 0)) : 0);
        } else {
          const res = await warehouseAPI.getInventory({ location_id: selectedSourceId });
          const inv = (res.data?.items || res.data?.inventory || res.data || []).find(i => i.item_id === selectedItem.id);
          setAvailableQty(inv ? (inv.qty_on_hand - (inv.reserved_quantity || 0) - (inv.quarantined_quantity || 0)) : 0);
        }
      } catch {
        setAvailableQty(null);
      }
      setLoadingAvail(false);
    };
    check();
  }, [selectedItem, selectedSourceId, sourceType]);

  const handleSubmit = async () => {
    if (!selectedItem) { toast.error('Select an item'); return; }
    if (!selectedSourceId) { toast.error('Select a source'); return; }
    const parsedQty = parseInt(qty, 10);
    if (!qty || isNaN(parsedQty) || parsedQty < 1) { toast.error('Qty must be a positive integer (>= 1)'); return; }

    setSubmitting(true);
    try {
      await jobMaterialsAPI.add(jobId, {
        item_id: selectedItem.id,
        qty: parsedQty,
        source_type: sourceType,
        source_vehicle_id: sourceType === 'vehicle' ? selectedSourceId : undefined,
        source_warehouse_id: sourceType === 'warehouse' ? selectedSourceId : undefined,
        notes: notes.trim() || undefined,
        idempotency_key: `${jobId}_${selectedItem.id}_${Date.now()}`,
      });
      toast.success('Material added');
      onAdded();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'object' && detail?.code === 'INSUFFICIENT_STOCK') {
        toast.error(`Insufficient stock. Available: ${detail.available}, Requested: ${detail.requested}`);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to add material');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const sources = sourceType === 'vehicle' ? vehicles : warehouses;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="add-material-modal">
        <DialogHeader>
          <DialogTitle>Add Material</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Source</Label>
            <Select value={sourceType} onValueChange={(v) => { setSourceType(v); setSelectedSourceId(''); setAvailableQty(null); }}>
              <SelectTrigger data-testid="source-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="vehicle">Vehicle</SelectItem>
                <SelectItem value="warehouse">Warehouse</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{sourceType === 'vehicle' ? 'Vehicle' : 'Warehouse'}</Label>
            <Select value={selectedSourceId} onValueChange={(v) => { setSelectedSourceId(v); setAvailableQty(null); }}>
              <SelectTrigger data-testid="source-select"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>
                {sources.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Item</Label>
            {selectedItem ? (
              <div className="flex items-center gap-2 p-2 bg-muted rounded-md">
                <span className="text-sm font-medium flex-1" data-testid="selected-item-name">{selectedItem.name}</span>
                {selectedItem.sku && <span className="text-xs text-muted-foreground">{selectedItem.sku}</span>}
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setSelectedItem(null); setItemSearch(''); setAvailableQty(null); }}>
                  <span className="text-lg leading-none">&times;</span>
                </Button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
                <Input value={itemSearch} onChange={(e) => setItemSearch(e.target.value)} placeholder="Search items..." className="pl-8" data-testid="item-search-input" />
                {searching && <Loader2 className="absolute right-2.5 top-2.5 w-4 h-4 animate-spin text-muted-foreground" />}
                {itemResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-48 overflow-auto">
                    {itemResults.map((item) => (
                      <button key={item.id} className="w-full text-left px-3 py-2 hover:bg-accent text-sm flex justify-between" onClick={() => { setSelectedItem(item); setItemSearch(''); setItemResults([]); }} data-testid={`item-option-${item.id}`}>
                        <span>{item.name}</span>
                        {item.sku && <span className="text-muted-foreground text-xs">{item.sku}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {selectedItem && selectedSourceId && (
            <div className="flex items-center gap-2 text-sm" data-testid="available-qty-display">
              {loadingAvail ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Checking availability...</>
              ) : availableQty != null ? (
                <>
                  <span className={`font-medium ${availableQty > 0 ? 'text-green-600' : 'text-red-600'}`}>Available: {availableQty}</span>
                  {availableQty <= 0 && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                </>
              ) : null}
            </div>
          )}
          <div>
            <Label>Quantity</Label>
            <Input type="number" min="1" step="1" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Enter qty..." data-testid="material-qty-input" />
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="material-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || !selectedItem || !selectedSourceId || !qty} data-testid="submit-material-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Add Material
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
